import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

export default forwardRef(function GLBViewer({ url, style, onLoadFinish, format }, ref) {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rootRef = useRef(null);
  // 最开始的贴图
  const rootMapRef = useRef(null);

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    snapshotBase64() {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      // 需要在 renderer 初始化时设置 preserveDrawingBuffer:true 才能截图
      const dataURL = rendererRef.current.domElement.toDataURL('image/png'); // 'data:image/png;base64,...'
      // 如果你只要纯 base64（无前缀）：
      // const base64 = dataURL.split(',')[1];
      return dataURL;
    },
    // 变成白膜
    applyWhite() {
      rootRef.current.traverse((obj) => {
        if (obj.isMesh) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m, i) => {
            if (!m) return;
            // 认为“无贴图”的条件：没有 m.map
            const whiteMat = new THREE.MeshStandardMaterial({
              color: 0xffffff,
              roughness: 0.6,
              metalness: 0.0,
            });
            // 如果你更想保留原材质其他属性，只改颜色也行：
            // m.color?.set(0xffffff); m.needsUpdate = true;
            if (Array.isArray(obj.material)) {
              obj.material[i] = whiteMat;
            } else {
              obj.material = whiteMat;
            }
          });
          obj.castShadow = obj.receiveShadow = true;
        }
      });
    },
    // 变成最开始的
    applyInit() {
      rootRef.current.traverse((obj) => {
        if (obj.isMesh) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m, i) => {
            obj.material = rootMapRef.current;
            // if (!m) return;
            // // 认为“无贴图”的条件：没有 m.map
            // const whiteMat = new THREE.MeshStandardMaterial({
            //   color: 0xffffff,
            //   roughness: 0.6,
            //   metalness: 0.0,
            // });
            // // 如果你更想保留原材质其他属性，只改颜色也行：
            // // m.color?.set(0xffffff); m.needsUpdate = true;
            // if (Array.isArray(obj.material)) {
            //   obj.material[i] = whiteMat;
            // } else {
            //   obj.material = whiteMat;
            // }
          });
          obj.castShadow = obj.receiveShadow = true;
        }
      });
    },
  }));
  useEffect(() => {
    const container = mountRef.current;

    // 场景
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 相机
    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000,
    );
    camera.position.set(0, 1, 3);
    cameraRef.current = camera;

    // 渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);

    // 控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // 平滑过渡
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.enableRotate = true;

    // 灯光
    // 添加灯光（必要）
    const ambientLight = new THREE.AmbientLight(0xffffff, 1); // 环境光
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 7.5);
    scene.add(directionalLight);

    const addModel = (model) => {
      rootRef.current = model;
      model.traverse((obj) => {
        if (obj.isMesh) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m, i) => {
            // if (!m) return;
            // 认为“无贴图”的条件：没有 m.map
            // if (!m.map) {
            rootMapRef.current = m;
            // }
          });
          obj.castShadow = obj.receiveShadow = true;
        }
      });
      applyWhiteIfNoMap(model);

      // 1. 计算模型包围盒
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size); // 模型尺寸
      box.getCenter(center); // 模型中心点


      // 2. 将模型移动到原点
      model.position.sub(center);
      // model.scale.set(0.01, 0.01, 0.01);

      // 3. 设置合适的相机位置（根据模型大小）
      const maxDim = Math.max(size.x, size.y, size.z);

      const fov = camera.fov * (Math.PI / 180); // 摄像机角度转弧度
      const cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      camera.position.set(0, 0, cameraZ * 1.5);
      camera.lookAt(0, 0, 0);

      // 4. 添加模型
      scene.add(model);

      frameObject(model);

      controls.update();
      onLoadFinish();
    };

    const loadGlb = () => {
      // 加载 GLB 模型
      const loader = new GLTFLoader();
      loader.load(url, (gltf) => {
        const model = gltf.scene;
        addModel(model);
      });
    };

    const loadFbx = () => {
      const loader = new FBXLoader();
      loader.load(
        url,
        (fbx) => {
          addModel(fbx);
        },
        undefined,
        (error) => {
          console.error('FBX 加载失败:', error);
        },
      );
    };

    // 让模型居中并调相机
    const frameObject = (object) => {
      const box = new THREE.Box3().setFromObject(object);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      // 模型放到原点
      object.position.sub(center);

      // 相机距离：根据包围盒对角线估个距离
      const maxDim = Math.max(size.x, size.y, size.z);
      const dist = maxDim / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
      camera.position.set(dist * 1.4, dist * 1.0, dist * 1.4);
      camera.lookAt(0, 0, 0);
      controls.target.set(0, 0, 0);
      controls.update();
    };

    // 给“无贴图”的材质套白膜
    const applyWhiteIfNoMap = (root) => {
      root.traverse((obj) => {
        if (obj.isMesh) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m, i) => {
            if (!m) return;
            // 认为“无贴图”的条件：没有 m.map
            if (!m.map) {
              // 1) 直接把材质改成白色金属度/粗糙度合理值（更像“白膜”）
              const whiteMat = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                roughness: 0.6,
                metalness: 0.0,
              });
              // 如果你更想保留原材质其他属性，只改颜色也行：
              // m.color?.set(0xffffff); m.needsUpdate = true;
              if (Array.isArray(obj.material)) {
                obj.material[i] = whiteMat;
              } else {
                obj.material = whiteMat;
              }
            }
          });
          obj.castShadow = obj.receiveShadow = true;
        }
      });
    };
    const loadObj = () => {
      const loader = new OBJLoader();
      loader.load(
        url,
        (obj) => {
          addModel(obj);
        },
        undefined,
        (error) => {
          console.error('OBJ 加载失败:', error);
        },
      );
    };

    if (format === 'glb') {
      loadGlb();
    } else if (format === 'fbx') {
      loadFbx();
    } else if (format === 'obj') {
      loadObj();
    }

    // 动画循环
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 监听尺寸变化
    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // 清理资源
    return () => {
      window.removeEventListener('resize', handleResize);
      container.removeChild(renderer.domElement);
      renderer.dispose();
      controls.dispose();
    };
  }, []);

  return <div ref={mountRef} style={style} />;
});
